﻿import {translation} from "@2d-front-monorepo/core";
import {
	DriverId, DriverResult,
	FullEntityRelation, IAllPermits,
	PermitResult, Permits,
	PermitStatusType,
	ResidencyId,
	ResidencyResult, VehicleId, VehicleResult
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX} from "react";
import {Link, useNavigate} from "react-router";
import {cancelPermit, cancelPermits} from "../../../hooks/cancelPermit";
import {toggleAutoRenewPermit} from "../../../hooks/toggleAutoRenewPermit";
import {formatRoute, PrivateRoutes} from "../../../routes";
import {PermitMenuProps} from "../../Registrations";

function canEditGlobal(permit: PermitResult): boolean {
	return permit.status.type === PermitStatusType.Active ||
		permit.status.type === PermitStatusType.Approved ||
		permit.status.type === PermitStatusType.AboutToExpire ||
		permit.status.type === PermitStatusType.Submitted;
}

function canEditDriver(driver: FullEntityRelation<DriverId, DriverResult>, permit: PermitResult): boolean {
	if (driver.changed != null) {
		return false;
	}

	return canEditGlobal(permit);
}

function canEditResidency(residency: FullEntityRelation<ResidencyId, ResidencyResult>, permit: PermitResult, permits: IAllPermits): boolean {
	if (residency.changed != null) {
		return false;
	}

	if (!canEditGlobal(permit)) {
		return false;
	}

	if (permit.status.type === PermitStatusType.Submitted) {
		return permits.permits.every(x => x.status.type === PermitStatusType.Submitted);
	}

	return Permits.isValidState(permit, true);
}

function canEditVehicles(vehicles: FullEntityRelation<VehicleId, VehicleResult>[], permit: PermitResult): boolean {
	if (vehicles.some(x => x.changed == null)) {
		return canEditGlobal(permit);
	}

	return false;
}

export default function DefaultPermitMenu(props: PermitMenuProps): JSX.Element {
	const {
		group,
		permits,
		driver,
		residency,
		vehicles,
		editDriverEnabled,
		editResidencyEnabled,
		editVehicleEnabled,
		cancelEnabled,
		unparkEnabled,
		autoRenewEnabled
	} = props;
	const {permit} = group;
	const hasVehicles = vehicles.length > 0;

	const grouped = group.count > 1;
	const navigate = useNavigate();
	const viewTitle = grouped ? PermitTranslations.view_all : PermitTranslations.view;
	const viewUrl = grouped ? formatRoute(PrivateRoutes.permitGroupView, group.name) : formatRoute(PrivateRoutes.permitView, permit.id);

	// const editDriverEnabledFinal = editDriverEnabled ?? (driver != null && canEditDriver(driver, permit));
	const editResidencyEnabledFinal = editResidencyEnabled ?? (residency != null && canEditResidency(residency, permit, permits));
	const editVehicleEnabledFinal = editVehicleEnabled ?? (hasVehicles && canEditVehicles(vehicles, permit));
	const cancelEnabledFinal = cancelEnabled ?? true;
	const unparkEnabledFinal = (unparkEnabled ?? false) && !grouped && Permits.isActive(permit);
	const autoRenewEnabledFinal = autoRenewEnabled === "automatic"
		? Permits.isValidState(permit, true) && !Permits.alreadyRenewed(permit)
		: autoRenewEnabled ?? false;

	if (grouped) {
		return <>
			<Link to={viewUrl}>{translation(viewTitle)}</Link>
			{cancelEnabledFinal
				? <button onClick={() => cancelPermits(group.permits.map(x => x.id), navigate)}>{translation(PermitTranslations.summary_page__cancel_permits)}</button>
				: undefined
			}
		</>
	}

	return <>
		<Link to={viewUrl}>{translation(viewTitle)}</Link>

		{/*{editDriverEnabledFinal &&*/}
		{/*    <Link to={`${PrivateRoutes.driversCreate}?permitId=${permit.id}&driverId=${driver?.original.id}`}>{translation(PermitTranslations.modify_address)}</Link>}*/}

		{editResidencyEnabledFinal &&
            <Link to={`${PrivateRoutes.residenciesCreate}?permitId=${permit.id}&residencyId=${residency?.original.id}`}>{translation(PermitTranslations.modify_address)}</Link>}

		{editVehicleEnabledFinal &&
            <Link to={`${PrivateRoutes.vehiclesCreate}?permitId=${permit.id}`}>{translation(PermitTranslations.modify_vehicle)}</Link>}

		{unparkEnabledFinal
			? <Link to={formatRoute(PrivateRoutes.permitsUnpark, permit.id)}>{translation(PermitTranslations.permit_unpark__button)}</Link>
			: undefined
		}

		{autoRenewEnabledFinal
			? <button onClick={() => toggleAutoRenewPermit(permit)}>
				{translation(Permits.isAutoRenewEnabled(permit) ? PermitTranslations.permit_menu__auto_renew__disable : PermitTranslations.permit_menu__auto_renew__enable)}
			</button>
			: undefined
		}

		{cancelEnabledFinal
			? <button onClick={() => cancelPermit(permit, navigate)}>{translation(PermitTranslations.summary_page__cancel_permit)}</button>
			: undefined
		}
	</>
}
